import { Button } from '@finn/design-system/atoms/button';
import { Spinner } from '@finn/design-system/atoms/spinner';
import { ArrowForwardIos } from '@finn/design-system/icons/arrow-forward-ios';
import { Features, useGetVariant } from '@finn/ua-featureflags';
import { TrackingEventName } from '@finn/ua-tracking';
import { cn, trackEvent } from '@finn/ui-utils';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { AiIcon } from './AiIcon';
import PlaceholderText from './PlaceholderText';

type AiResponse = {
  url: string;
  total_results: number;
  not_found?: boolean;
  wrong_prompt?: boolean;
  help_center?: boolean;
  reason?: string;
};

export const AiSearch: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  const [prompt, setPrompt] = useState('');
  const [firedSearchStartedEvent, setFiredSearchStartedEvent] = useState(false);
  const textArea = useRef<HTMLTextAreaElement>(null);
  const [isExecutingAi, setIsExecutingAi] = useState(false);
  const [searchFocused, setIsSearchFocused] = useState(false);
  const { push } = useRouter();

  const handleSearch = async () => {
    textArea.current?.blur();

    if (!prompt) {
      return;
    }
    setIsExecutingAi(true);

    trackEvent(TrackingEventName.SEARCH_SUBMITTED, {
      query: prompt,
    });

    setTimeout(async () => {
      try {
        const response = await fetch('/api/ai-search', {
          method: 'POST',
          body: JSON.stringify({ message: prompt }),
        });

        const {
          url,
          total_results,
          not_found,
          wrong_prompt,
          reason,
          help_center,
        } = (await response.json()) as AiResponse;

        const encodedPrompt = encodeURIComponent(prompt);

        trackEvent(TrackingEventName.SEARCH_RESULT, {
          query: encodedPrompt,
          total_results,
          not_found,
          help_center,
          wrong_prompt,
          reason,
        });

        const reasonUrl = `${url?.includes('?') ? '&' : '?'}reason=${reason ? encodeURIComponent(reason) : ''}`;

        if (help_center) {
          setTimeout(() => {
            window.location.href = `https://support.finn.com/hc/de-de/search?utf8=%E2%9C%93&query=${encodedPrompt}`;
          }, 100);

          return;
        }

        if (url) {
          push(
            `${url}${reasonUrl}&reason_type=success&prompt=${encodedPrompt}`
          );

          setIsExecutingAi(false);

          return;
        }

        if (not_found) {
          push(`/subscribe?reason_type=not_found&prompt=${encodedPrompt}`);

          setIsExecutingAi(false);

          return;
        }
        if (wrong_prompt) {
          push(
            `/subscribe${reasonUrl}&reason_type=wrong_prompt&prompt=${encodedPrompt}`
          );

          setIsExecutingAi(false);

          return;
        }
        if (total_results === 0) {
          push(`/subscribe?reason_type=no_results&prompt=${encodedPrompt}`);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsExecutingAi(false);
      }
    });
  };

  const searchParams = useSearchParams();
  const initialPrompt = searchParams.get('prompt');

  useEffect(() => {
    setPrompt(initialPrompt || '');
  }, [initialPrompt]);

  const isBVariant = useGetVariant(Features.ExpPlpAiSearch) === 'b';

  const showTextarea = searchFocused || prompt.length > 0;

  return (
    <form
      action={handleSearch}
      className={cn(
        'relative bg-white duration-300',
        (prompt.length || (!searchFocused && isBVariant)) &&
          '[&_#placeholder]:opacity-0',
        (!isMobile || showTextarea) && 'w-full',
        isMobile && !showTextarea && 'w-[calc(100%-60px)]'
      )}
    >
      {isExecutingAi && (
        <div
          className={
            'absolute left-0 top-0 z-overlay size-full animate-pulse border-2 border-trustedBlue shadow-highlightTrustedBlue'
          }
        />
      )}
      <div
        className={cn(
          'relative h-[56px] border border-transparent transition-all duration-300',
          (showTextarea || !isBVariant) && !isMobile && 'h-[100px]',
          (showTextarea || !isBVariant) && 'border-black'
        )}
      >
        <div className="absolute left-0 right-12 top-0 h-full p-4">
          <PlaceholderText isMobile={isMobile} />
          <textarea
            rows={isMobile && 1}
            ref={textArea}
            onFocus={() => {
              setIsSearchFocused(true);
            }}
            onBlur={() => {
              setIsSearchFocused(false);
            }}
            className={cn(
              'absolute left-0 top-0 z-overlay size-full bg-transparent p-4',
              'box-border border-none pr-0 outline-none',
              `focus resize-none body-16-regular disabled:bg-snow disabled:text-iron`
            )}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            value={prompt}
            onChange={(e) => {
              if (!firedSearchStartedEvent) {
                setFiredSearchStartedEvent(true);
                trackEvent(TrackingEventName.SEARCH_STARTED, {});
              }

              setPrompt(e.target.value.replaceAll('\n', ''));
            }}
          />
        </div>

        <Button
          type="submit"
          className={`absolute bottom-3 right-3 size-8 rounded-full p-0 ${showTextarea && 'z-[100]'}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            handleSearch();
          }}
        >
          {isExecutingAi ? (
            <Spinner className="!size-5 animate-spin fill-white" />
          ) : (
            <ArrowForwardIos className="!size-5" />
          )}
        </Button>
        {isBVariant && (
          <Button
            className={cn(
              'absolute left-0 top-0 w-full transition-opacity duration-150',
              !showTextarea && 'z-[100]',
              showTextarea && 'z-[-100] opacity-0'
            )}
            variant="primary"
            onClick={() => textArea.current?.focus()}
          >
            <AiIcon className="mr-2" /> Suchen mit KI
          </Button>
        )}
      </div>
    </form>
  );
};
